// The trip and violation searches both scan `trips` over a date window, so an
// unbounded range is what turns a search into a full-table scan the gateway
// cuts off at 29 s. One year is the longest window the pages accept; 366 days
// admits every calendar year, leap year included.
export const MAX_QUERY_RANGE_DAYS = 366

export const MAX_QUERY_RANGE_MESSAGE =
  'Searches are limited to one year or less. Please choose a start and end date within 12 months.'

export const isCalendarDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000')) return false
  const timestamp = Date.parse(`${value}T00:00:00Z`)
  // Date.parse normalizes impossible days such as February 30 into March.
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === value
}

// Inclusive day count of a `yyyy-MM-dd` range. Parsing as UTC keeps a
// daylight-saving day from rounding to the wrong count. An unparseable or
// inverted range has no usable length, so it reports Infinity rather than a
// negative or NaN one.
export const inclusiveDateRangeDays = (startDate: string, endDate: string): number => {
  if (!isCalendarDate(startDate) || !isCalendarDate(endDate)) return Number.POSITIVE_INFINITY
  const start = Date.parse(`${startDate}T00:00:00Z`)
  const end = Date.parse(`${endDate}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return Number.POSITIVE_INFINITY
  return Math.round((end - start) / (24 * 60 * 60 * 1_000)) + 1
}

// An inverted or malformed range is a different problem than a too-long one,
// so it is deliberately not reported here.
export const exceedsMaxQueryRange = (startDate: string, endDate: string): boolean => {
  const days = inclusiveDateRangeDays(startDate, endDate)
  return Number.isFinite(days) && days > MAX_QUERY_RANGE_DAYS
}

export const getDateRangeError = (startDate: string, endDate: string): string | null => {
  if (!startDate || !endDate) return 'Start and end dates are required.'
  if (!isCalendarDate(startDate) || !isCalendarDate(endDate)) return 'Enter valid start and end dates.'
  if (startDate > endDate) return 'Start date must be on or before end date.'
  return exceedsMaxQueryRange(startDate, endDate) ? MAX_QUERY_RANGE_MESSAGE : null
}
